import { StorageUtil } from '../../../shared/utils/storage';
import {
  COMPANY_PROFILE_MODAL_SIZE,
  V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY,
  V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_COMPANY,
} from '../helpers/helper';

const V2_LEAD_FINDER_COMPANY_ID_NAME_MAP_STORAGE_KEY =
  'v2LeadFinderCompanyIdNameMap';

/**
 * Get the company ID -> name map for a user from storage.
 */
export const getCompanyIdNameMap = (
  userEmail: string,
): Record<string, string> => {
  if (!userEmail) return {};
  try {
    const stored = StorageUtil.getItem(
      V2_LEAD_FINDER_COMPANY_ID_NAME_MAP_STORAGE_KEY,
    );
    if (!stored) return {};
    const all = JSON.parse(stored) || {};
    return all[userEmail] || {};
  } catch {
    return {};
  }
};

/**
 * Merge new mappings into storage for the user.
 */
export const addCompanyMappings = (
  userEmail: string,
  mappings: Record<string, string>,
): void => {
  if (!userEmail || !mappings || Object.keys(mappings).length === 0) return;
  try {
    const stored = StorageUtil.getItem(
      V2_LEAD_FINDER_COMPANY_ID_NAME_MAP_STORAGE_KEY,
    );
    const all = stored ? JSON.parse(stored) || {} : {};
    const existing = all[userEmail] || {};
    all[userEmail] = { ...existing, ...mappings };
    StorageUtil.setItem(
      V2_LEAD_FINDER_COMPANY_ID_NAME_MAP_STORAGE_KEY,
      JSON.stringify(all),
    );
  } catch {
    // Silently fail if storage is not available
  }
};

/**
 * Remove company IDs from the user's map that are not in allCompanyIds.
 */
export const pruneCompanyMap = (
  userEmail: string,
  allCompanyIds: Set<string>,
): void => {
  if (!userEmail) return;
  try {
    const stored = StorageUtil.getItem(
      V2_LEAD_FINDER_COMPANY_ID_NAME_MAP_STORAGE_KEY,
    );
    if (!stored) return;
    const all = JSON.parse(stored) || {};
    const current = all[userEmail] || {};
    const pruned: Record<string, string> = {};
    Object.keys(current).forEach((id) => {
      if (allCompanyIds.has(id)) {
        pruned[id] = current[id];
      }
    });
    all[userEmail] = pruned;
    StorageUtil.setItem(
      V2_LEAD_FINDER_COMPANY_ID_NAME_MAP_STORAGE_KEY,
      JSON.stringify(all),
    );
  } catch {
    // Silently fail
  }
};

/**
 * Clear the company ID-name map from storage (e.g. on logout).
 */
export const clearCompanyIdNameMap = (): void => {
  try {
    StorageUtil.removeItem(V2_LEAD_FINDER_COMPANY_ID_NAME_MAP_STORAGE_KEY);
  } catch {
    // Silently fail if storage is not available
  }
};

/**
 * Extract all company_ids from payloads (company_ids.includes and company_ids.excludes only).
 */
export const extractCompanyIdsFromPayloads = (payloads: any[]): Set<string> => {
  const ids = new Set<string>();
  payloads.forEach((entry) => {
    const payload = entry?.payload ?? entry;
    if (!payload || typeof payload !== 'object') return;
    const companyIds = payload.company_ids;
    if (!companyIds) return;
    if (companyIds.includes && Array.isArray(companyIds.includes)) {
      companyIds.includes.forEach((id: number | string) => ids.add(String(id)));
    }
    if (companyIds.excludes && Array.isArray(companyIds.excludes)) {
      companyIds.excludes.forEach((id: number | string) => ids.add(String(id)));
    }
  });
  return ids;
};

/**
 * Get all payloads from both People and Company recent search storages for a user.
 */
export const getAllRecentSearchPayloads = (
  userEmail: string,
): Array<{ payload: any }> => {
  if (!userEmail) return [];
  const result: Array<{ payload: any }> = [];
  try {
    [
      V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY,
      V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_COMPANY,
    ].forEach((key) => {
      const stored = StorageUtil.getItem(key);
      if (!stored) return;
      const parsed = JSON.parse(stored) || {};
      const list = parsed[userEmail] || [];
      list.forEach((item: any) => {
        if (item?.payload) result.push({ payload: item.payload });
      });
    });
  } catch {
    // ignore
  }
  return result;
};

export const clearCompanyProfileModalSize = () =>
  localStorage.removeItem(COMPANY_PROFILE_MODAL_SIZE);
